import { Button, Field, Input } from '@nolens/ui';
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { NolensMark } from '../app/Brand';
import { useSession } from '../lib/session';
import { auth } from '../lib/supabase';
import { DEV_LOGIN_ENABLED } from '../lib/devAuth';

type Step = { kind: 'email' } | { kind: 'code'; email: string } | { kind: 'password' };

/**
 * Utentes: código por email, sem palavra-passe (ADR-021).
 * Equipa: o mesmo código por email (ou palavra-passe) e depois o segundo fator TOTP (/verificacao).
 * Contas criadas apenas por convite: nunca cria utilizadores aqui.
 */
export function LoginPage() {
  const { session } = useSession();
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (session) return <Navigate to="/" replace />;
  if (DEV_LOGIN_ENABLED) return <Navigate to="/dev/entrar" replace />;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const normalized = email.trim().toLowerCase();
      const { error } = await auth!.signInWithOtp({
        email: normalized,
        options: { shouldCreateUser: false },
      });
      // Mesma resposta exista ou não a conta (evita enumeração de emails).
      if (error && error.status !== 422 && error.status !== 400) {
        setError('Não foi possível enviar o código. Tente novamente dentro de momentos.');
        return;
      }
      setStep({ kind: 'code', email: normalized });
    });
  };

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'code') return;
    void run(async () => {
      const { error } = await auth!.verifyOtp({ email: step.email, token: code.trim(), type: 'email' });
      if (error) setError('O código não é válido ou expirou. Peça um novo código.');
    });
  };

  const signInWithPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { error } = await auth!.signInWithPassword({ email: email.trim().toLowerCase(), password });
      if (error) setError('Email ou palavra-passe incorretos.');
    });
  };

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex justify-center">
          <NolensMark endorsement />
        </div>

        {step.kind === 'email' && (
          <form onSubmit={sendCode} className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Entrar</h1>
              <p className="mt-2 text-[15px] text-text-2">Enviamos um código para o seu email. Não precisa de palavra-passe.</p>
            </div>
            <Field label="Email" error={error}>
              {(p) => (
                <Input {...p} type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              )}
            </Field>
            <Button type="submit" size="lg" fullWidth loading={busy}>
              Enviar código
            </Button>
            <button type="button" className="text-sm text-text-2 underline-offset-4 hover:underline" onClick={() => setStep({ kind: 'password' })}>
              Sou profissional — entrar com palavra-passe
            </button>
          </form>
        )}

        {step.kind === 'code' && (
          <form onSubmit={verifyCode} className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Verifique o seu email</h1>
              <p className="mt-2 text-[15px] text-text-2">
                Se existir uma conta para <strong className="font-medium text-text-1">{step.email}</strong>, enviámos um código de 6
                dígitos. Também pode abrir o link do email.
              </p>
            </div>
            <Field label="Código" error={error}>
              {(p) => (
                <Input
                  {...p}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  className="text-center text-xl tracking-[0.4em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              )}
            </Field>
            <Button type="submit" size="lg" fullWidth loading={busy}>
              Entrar
            </Button>
            <button type="button" className="text-sm text-text-2 hover:underline" onClick={() => setStep({ kind: 'email' })}>
              Usar outro email
            </button>
          </form>
        )}

        {step.kind === 'password' && (
          <form onSubmit={signInWithPassword} className="flex flex-col gap-5">
            <h1 className="text-2xl font-semibold tracking-tight">Entrar como profissional</h1>
            <Field label="Email">
              {(p) => <Input {...p} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />}
            </Field>
            <Field label="Palavra-passe" error={error}>
              {(p) => (
                <Input {...p} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              )}
            </Field>
            <Button type="submit" size="lg" fullWidth loading={busy}>
              Entrar
            </Button>
            <button type="button" className="text-sm text-text-2 hover:underline" onClick={() => setStep({ kind: 'email' })}>
              Entrar com código por email
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
